/**
 * LotDetailModal — History + Scan Details modal for a raw material lot.
 * Extracted from Dashboard.tsx for maintainability.
 */
import React, { useState } from 'react';
import { X, History, ScanLine, Loader2 } from 'lucide-react';
import { Lot, LotScanDetail, InventoryTransaction } from '../../../services/inventoryService';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatDateTime as formatDateTimeUtil } from '../../../utils/dateUtils';
import { TX_TYPE_COLORS, getTxTypeLabels } from '../inventoryHelpers';
import FreshnessBadge from '../../../components/FreshnessBadge';

interface LotDetailModalProps {
  lot: Lot;
  scanDetails: LotScanDetail[];
  scanDetailsLoading: boolean;
  txData: InventoryTransaction[];
  txLoading: boolean;
  txPage: number;
  txTotal: number;
  txTotalPages: number;
  onLoadTxPage: (page: number) => void;
  onClose: () => void;
}

const LotDetailModal: React.FC<LotDetailModalProps> = ({
  lot,
  scanDetails,
  scanDetailsLoading,
  txData,
  txLoading,
  txPage,
  txTotal,
  txTotalPages,
  onLoadTxPage,
  onClose,
}) => {
  const { t } = useLanguage();
  const formatDateTime = (dateString?: string) => formatDateTimeUtil(dateString);
  const txTypeColors = TX_TYPE_COLORS;
  const txTypeLabels = getTxTypeLabels(t);
  const [activeTab, setActiveTab] = useState<'history' | 'scanDetails'>('history');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
              {lot.item_number} — {lot.item_name || '-'}
              {lot.chinese_name && ` (${lot.chinese_name})`}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-secondary-500 dark:text-gray-400">
                Transfer #: {lot.transfer_number}
              </p>
              {lot.status === 'by_product_p' && lot.freshness_badge_key && (
                <FreshnessBadge
                  ageDays={lot.freshness_age_days}
                  badgeKey={lot.freshness_badge_key}
                  badgeLabel={lot.freshness_badge_label}
                  badgeColor={lot.freshness_badge_color}
                />
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-secondary-100 dark:hover:bg-gray-700 text-secondary-500 dark:text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Inner Tabs */}
        <div className="border-b border-secondary-200 dark:border-gray-700">
          <nav className="flex -mb-px px-6">
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'history'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              <History className="w-4 h-4 inline-block mr-1.5" />
              {t('inventory.transactionHistory')}
            </button>
            <button
              onClick={() => setActiveTab('scanDetails')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'scanDetails'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              <ScanLine className="w-4 h-4 inline-block mr-1.5" />
              {t('inventory.scanDetails')}
            </button>
          </nav>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* History Tab */}
          {activeTab === 'history' && (
            <>
              {txLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-secondary-500 dark:text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('common.loading')}
                </div>
              ) : txData.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-secondary-500 dark:text-gray-400 uppercase border-b border-secondary-200 dark:border-gray-700">
                      <th className="pb-2 text-left">{t('dateTime.date')}</th>
                      <th className="pb-2 text-left">{t('inventory.transactionType')}</th>
                      <th className="pb-2 text-left">{t('inventory.boxLookupSession')}</th>
                      <th className="pb-2 text-right">{t('inventory.change')}</th>
                      <th className="pb-2 text-right">{t('inventory.balance')}</th>
                      <th className="pb-2 text-left pl-4">{t('production.notes')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 dark:divide-gray-700">
                    {txData.map((tx) => (
                      <tr key={tx.id}>
                        <td className="py-2 whitespace-nowrap text-secondary-900 dark:text-white">
                          {formatDateTime(tx.created_at)}
                        </td>
                        <td className="py-2 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${txTypeColors[tx.transaction_type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {txTypeLabels[tx.transaction_type] || tx.transaction_type}
                          </span>
                        </td>
                        <td className="py-2 whitespace-nowrap text-primary-600 dark:text-primary-400">
                          {tx.session_number || tx.notes || '-'}
                        </td>
                        <td className={`py-2 whitespace-nowrap text-right font-semibold ${Number(tx.quantity_change) > 0
                          ? 'text-green-600 dark:text-green-400'
                          : Number(tx.quantity_change) < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-secondary-500 dark:text-gray-400'
                          }`}>
                          {(() => {
                            const unitWt = Number(lot?.unit_weight) || 0;
                            const subQty = Number(lot?.item_sub_quantity) || 0;
                            const isPacking = lot?.is_packing_material === 'Y' && unitWt > 0 && subQty > 0;
                            const raw = Number(tx.quantity_change);
                            const val = isPacking ? Math.round((raw / unitWt) * subQty) : raw;
                            return `${val > 0 ? '+' : ''}${isPacking ? val : val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${isPacking ? ' pcs' : ''}`;
                          })()}
                        </td>
                        <td className="py-2 whitespace-nowrap text-right text-secondary-900 dark:text-white">
                          {(() => {
                            if (tx.balance_after == null) return '-';
                            const unitWt = Number(lot?.unit_weight) || 0;
                            const subQty = Number(lot?.item_sub_quantity) || 0;
                            const isPacking = lot?.is_packing_material === 'Y' && unitWt > 0 && subQty > 0;
                            const raw = Number(tx.balance_after);
                            const val = isPacking ? Math.round((raw / unitWt) * subQty) : raw;
                            return `${isPacking ? val : val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${isPacking ? ' pcs' : ''}`;
                          })()}
                        </td>
                        <td className="py-2 pl-4 text-secondary-500 dark:text-gray-400 max-w-[200px] truncate" title={tx.session_number ? (tx.notes || '') : ''}>
                          {tx.session_number ? (tx.notes || '-') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-8 text-secondary-500 dark:text-gray-400">{t('inventory.noTransactions')}</p>
              )}
            </>
          )}

          {/* Scan Details Tab */}
          {activeTab === 'scanDetails' && (
            <>
              {scanDetailsLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-secondary-500 dark:text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('common.loading')}
                </div>
              ) : scanDetails.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-secondary-500 dark:text-gray-400 uppercase border-b border-secondary-200 dark:border-gray-700">
                      <th className="pb-2 text-left">{t('inventory.box')} #</th>
                      <th className="pb-2 text-right">{t('inventory.boxLookupWeight')}</th>
                      <th className="pb-2 text-center">{t('common.status')}</th>
                      <th className="pb-2 text-left">GTIN</th>
                      <th className="pb-2 text-left">{t('inventory.remark')}</th>
                      <th className="pb-2 text-left">{t('inventory.scannedAt')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 dark:divide-gray-700">
                    {scanDetails.map((detail) => (
                      <tr key={detail.scan_order}>
                        <td className="py-2 text-secondary-700 dark:text-gray-300">{detail.scan_order}</td>
                        <td className="py-2 text-right text-secondary-900 dark:text-white">
                          {Number(detail.weight).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs
                        </td>
                        <td className="py-2 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${detail.status === 'accepted' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            detail.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                            {detail.status}
                          </span>
                        </td>
                        <td className="py-2 text-secondary-500 dark:text-gray-400 font-mono text-xs">{detail.gtin || '-'}</td>
                        <td className="py-2 text-secondary-500 dark:text-gray-400">{detail.remark || '-'}</td>
                        <td className="py-2 text-secondary-500 dark:text-gray-400 whitespace-nowrap">{formatDateTime(detail.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-8 text-secondary-500 dark:text-gray-400">{t('inventory.noScanDetails')}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-secondary-200 dark:border-gray-700 flex items-center justify-between">
          {activeTab === 'history' && txTotalPages > 1 ? (
            <>
              <p className="text-sm text-secondary-500 dark:text-gray-400">
                {t('common.showing')} {((txPage - 1) * 20) + 1}-{Math.min(txPage * 20, txTotal)} {t('common.of')} {txTotal}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => onLoadTxPage(txPage - 1)}
                  disabled={txPage === 1}
                  className="px-3 py-1 text-sm border border-secondary-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  {t('common.previous')}
                </button>
                <button
                  onClick={() => onLoadTxPage(txPage + 1)}
                  disabled={txPage === txTotalPages}
                  className="px-3 py-1 text-sm border border-secondary-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  {t('common.next')}
                </button>
              </div>
            </>
          ) : (
            <div />
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-gray-300 bg-secondary-100 dark:bg-gray-700 rounded-md hover:bg-secondary-200 dark:hover:bg-gray-600"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LotDetailModal;
